import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Group, Layout, Page, Tab } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, usePage, useAuth } from "@/comn/hooks";
import { APIS, SF_ORG_USR_INFO, URLS } from "@/tz/go/egaz/osci/ptl/usr/info/services/OsciAcrdtUsrService";

/*
 *  User Management
 * !== 사용자 관리 등록 ==!
 */
export const OSCI0103001S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth();

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }, { area: "cntyCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        usrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: { frgnrYn: "N" },
        }),
    };

    const psnTin = form.usrInfo.watch("psnTin");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Organization User Information !== 기관 사용자 정보 등록 ==!
        createOrgUsrInfo: useFetch({
            api: (data: any) => APIS.createOrgUsrInfo(data),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00074", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        if (comnUtils.isEmpty(auth.get("usrId"))) {
                            navigate(`/login`);
                        } else {
                            location.reload();
                        }
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),

        getUsrDuplCheck: useFetch({
            api: (data: any) => APIS.getUsrDuplCheck(data),
            enabled: false,
            onSuccess: (data) => {
                if (!data.usrDuplCheck.content) {
                    form.usrInfo.clearErrors("psnTin");
                } else {
                    form.usrInfo.setError("psnTin", {
                        message: "msg.osc.00232", // Please check your TIN. !== TIN을 조회해주세요. ==!
                        type: "error",
                    });

                    // form.usrInfo.setValue("psnTin", null);
                    // form.usrInfo.setValue("usrNm", null);
                    // form.usrInfo.setValue("usrMoblNo", null);
                    // form.usrInfo.setValue("usrEmlAddr", null);
                    // form.usrInfo.setValue("rsdtAddr", null);
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Organization Save Button !== 기관 저장 버튼 클릭 ==!
        onOrgSaveBtnClick: () => {
            // if (CSTM_OFCE_CD_RQRD_ARR.includes(orgCd)) {
            //     if (comnUtils.isEmpty(form.usrInfo.getValue("cstmOfceCd"))) {
            //         form.usrInfo.setError("cstmOfceCd", {
            //             message: "msg.osc.00013", // Mandatory fields !== 필수 입력 필드 ==!
            //             type: "error",
            //         });
            //         return;
            //     }
            // }
            if (!comnUtils.isEmpty(form.usrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            // save Information of Organization !== 기관 정보 저장 ==!
            form.usrInfo.handleSubmit(() => {
                const data = form.usrInfo.getValues();
                modal.openModal({
                    content: "msg.osc.00092", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                    onConfirm: () => {
                        fetch.createOrgUsrInfo.fetch(data);
                    },
                });
            })();
        },

        setCoTinChange: () => {
            const psnTin = form.usrInfo.getValue("psnTin");

            if (!comnUtils.isEmpty(psnTin)) {
                form.usrInfo.setError("psnTin", {
                    message: "msg.osc.00222", // Please check your TIN. !== TIN을 조회해주세요. ==!
                    type: "error",
                });
            }
        },

        checkPwd: () => {
            const usrPwd = form.usrInfo.getValue("usrPwd");
            const usrPwdCfrm = form.usrInfo.getValue("usrPwdCfrm");

            if (!comnUtils.isEmpty(usrPwd) && !comnUtils.equals(usrPwd, usrPwdCfrm)) {
                form.usrInfo.setError("usrPwdCfrm", {
                    message: "msg.osc.00210", // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                    type: "error",
                });
            } else {
                form.usrInfo.clearErrors("usrPwdCfrm");
            }
        },

        checkUsrTin: () => {
            const psnTin = form.usrInfo.getValue("psnTin");
            if (psnTin) {
                fetch.getUsrDuplCheck.fetch(psnTin);
            } else {
                form.usrInfo.clearErrors("psnTin");
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);
    const orgCd = form.usrInfo.watch("orgCd");

    return (
        <Group>
            <Group.Body>
                <Page>
                    <Group>
                        <Group.Body>
                            <Layout>
                                <Layout.Left>
                                    <Group.Title title={"T_USR_INFO"} titleSize={1}></Group.Title>
                                </Layout.Left>
                            </Layout>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control
                                        {...form.usrInfo.schema.psnTin}
                                        rightButton={{
                                            icon: "search",
                                            onClick: () => {
                                                const usrNo = form.usrInfo.getValue("psnTin");
                                                if (usrNo?.length == 9) {
                                                    form.usrInfo.clearErrors("psnTin");
                                                    handler.checkUsrTin();
                                                } else {
                                                    form.usrInfo.setError("psnTin", {
                                                        message: "msg.osc.00076", // TIN must be 9 digits. !== TIN은 9자리여야 합니다. ==!
                                                        type: "error",
                                                    });
                                                }
                                            },
                                        }}
                                        onChange={() => {
                                            handler.setCoTinChange();
                                        }}
                                        maxLength={9}
                                    />
                                    <Group.Control {...form.usrInfo.schema.usrNm} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control
                                        {...form.usrInfo.schema.orgCd}
                                        popupParams={{ lpcoIssuYn: "Y", orgAbrtYn: "Y" }}
                                        // onChange={(event) => {
                                        //     if (CSTM_OFCE_CD_RQRD_ARR.includes(event)) {
                                        //         if (comnUtils.isEmpty(form.usrInfo.getValue("cstmOfceCd"))) {
                                        //             form.usrInfo.setError("cstmOfceCd", {
                                        //                 message: "msg.osc.00013", // Mandatory fields !== 필수 입력 필드 ==!
                                        //                 type: "error",
                                        //             });
                                        //         }
                                        //     } else {
                                        //         form.usrInfo.clearErrors("cstmOfceCd");
                                        //     }
                                        // }}
                                    />
                                    <Group.Control
                                        {...form.usrInfo.schema.deptCd}
                                        popupParams={{ orgCd: orgCd }}
                                        filter={(_option: any, division: any) => division?.orgCd === orgCd}
                                    />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrInfo.schema.usrPwd} />
                                    <Group.Control
                                        {...form.usrInfo.schema.usrPwdCfrm}
                                        onBlur={() => {
                                            handler.checkPwd();
                                        }}
                                    />
                                </Group.Row>
                                {/* Display the password policy before submission so that users can enter a valid password. */}
                                <Group.Row>
                                    <Group.Any anySize={12} align="left">
                                        <span className="text-sm text-gray-500">{t("msg.osc.00225")}</span>
                                    </Group.Any>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrInfo.schema.orgRppnYn} />
                                    {/* <Group.Control {...form.usrInfo.schema.frgnrYn} /> */}
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrInfo.schema.aplcCntyCd} />
                                    <Group.Control {...form.usrInfo.schema.usrMoblNo} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrInfo.schema.usrEmlAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrInfo.schema.rsdtAddr} />
                                </Group.Row>
                                {/* <Group.Row>
                                    <Group.Control
                                        {...form.usrInfo.schema.cstmOfceCd}
                                        callback={(event) => {
                                            form.usrInfo.clearErrors("cstmOfceCd");
                                        }}
                                    />
                                    <Group.Cell size={6}>{t("msg.osc.00249")}</Group.Cell>
                                </Group.Row> */}
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Right>
                                    <Layout.Button role={"submit"} onClick={handler.onOrgSaveBtnClick} />
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Page>
            </Group.Body>
        </Group>
    );
};
